import { Zip, ZipDeflate, ZipPassThrough, strToU8 } from "fflate";

const MEMORY_LIMIT = 64 * 1024 * 1024;
const PAGE_LIMIT = 48 * 1024 * 1024;
const ARCHIVE_LIMIT = 1024 * 1024 * 1024;
const FILE_LIMIT = 12000;
const imageFields = /^(image_url|imageUrl|logo_url|logo_compact_url|icon_logo_url|loading_logo_url|cover_url|kiosk_dine_in_image_url|kiosk_takeaway_image_url)$/;
const cancelled = () => new DOMException("Descarga cancelada.", "AbortError");
const hash = async bytes => Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)))
  .map(byte => byte.toString(16).padStart(2, "0")).join("");

// The picker must run in the click handler, before the first network request.
export async function chooseRestaurantArchiveDestination() {
  const filename = `mozzo-datos-${new Date().toISOString().slice(0, 10)}.zip`;
  if (typeof window.showSaveFilePicker === "function") {
    const handle = await window.showSaveFilePicker({
      suggestedName: filename, types: [{ description: "Archivo ZIP", accept: { "application/zip": [".zip"] } }],
    });
    const writer = await handle.createWritable();
    return { write: data => writer.write(data), close: () => writer.close(), abort: () => writer.abort() };
  }
  let chunks = [], size = 0;
  return {
    async write(data) {
      size += data.length;
      if (size > MEMORY_LIMIT) throw new Error("La descarga supera el limite de este navegador. Usa Chrome o Edge en una computadora, o solicita una exportacion asistida. No se entrego un archivo incompleto.");
      chunks.push(data.slice());
    },
    async close() {
      const url = URL.createObjectURL(new Blob(chunks, { type: "application/zip" }));
      chunks = [];
      const link = document.createElement("a");
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    },
    async abort() { chunks = []; },
  };
}

export async function buildRestaurantDataArchive({ companyId, fetchPage, destination, signal, onProgress = () => {} }) {
  let pending = Promise.resolve(), failure, written = 0, fileCount = 0, rowCount = 0, assetCount = 0;
  const files = [], counts = {};
  const check = () => { if (signal?.aborted) throw cancelled(); if (failure) throw failure; };
  const zip = new Zip((error, data) => {
    if (error) { failure = error; return; }
    written += data.length;
    if (written > ARCHIVE_LIMIT) { failure = new Error("El archivo supera el limite de 1 GB. Solicita una exportacion asistida; no se entrego un archivo incompleto."); return; }
    pending = pending.then(() => { check(); return destination.write(data); }).catch(e => { failure = e; });
  });
  const add = async (name, bytes, compress = true, inventory = true) => {
    check();
    if (++fileCount > FILE_LIMIT) throw new Error("La descarga requiere una exportacion asistida por su cantidad de archivos. No se entrego un archivo incompleto.");
    const checksum = await hash(bytes);
    const entry = compress ? new ZipDeflate(name, { level: 1 }) : new ZipPassThrough(name);
    zip.add(entry);
    if (!bytes.length) entry.push(bytes, true);
    for (let i = 0; i < bytes.length; i += 262144) {
      check(); entry.push(bytes.subarray(i, i + 262144), i + 262144 >= bytes.length);
      await pending; check();
    }
    await pending; check();
    if (inventory) files.push({ path: name, bytes: bytes.length, sha256: checksum });
  };
  const extractImages = async (value, location, references) => {
    if (!value || typeof value !== "object") return;
    for (const [key, current] of Object.entries(value)) {
      check();
      const field = `${location}.${key}`;
      if (imageFields.test(key) && typeof current === "string" && current) {
        const embedded = /^data:image\/(png|jpeg|webp|gif);base64,([A-Za-z0-9+/=\r\n]+)$/.exec(current);
        if (embedded) {
          if (embedded[2].length > 12 * 1024 * 1024) throw new Error("Una imagen supera el limite de descarga. Solicita asistencia; no se entrego un archivo incompleto.");
          const bytes = Uint8Array.from(atob(embedded[2]), char => char.charCodeAt(0));
          const name = `imagenes/${String(++assetCount).padStart(6, "0")}.${embedded[1] === "jpeg" ? "jpg" : embedded[1]}`;
          await add(name, bytes, false);
          value[key] = { archiveFile: name };
          references.push({ field, status: "included", path: name });
        } else {
          let reference = null;
          try {
            const url = new URL(current);
            if (["https:", "http:"].includes(url.protocol) && !url.username && !url.password) {
              // Signed query strings are not exported as reusable credentials.
              url.search = ""; url.hash = ""; reference = url.href;
            }
          } catch (_) {
            if (current.startsWith("/") && !current.startsWith("//")) reference = current.split(/[?#]/)[0];
          }
          value[key] = { externalReference: reference, included: false };
          references.push({ field, status: "not_included", reference,
            reason: reference ? "external_file_no_download" : "unsupported_image_reference" });
        }
      } else if (current && typeof current === "object") await extractImages(current, field, references);
    }
  };
  try {
    check();
    const manifest = await fetchPage("manifest", null, signal);
    if (manifest.formatVersion !== 2 || manifest.companyId !== companyId ||
        !Array.isArray(manifest.datasets) || new Set(manifest.datasets).size !== manifest.datasets.length ||
        manifest.datasets.some(name => !/^[a-z][a-z0-9_]*$/.test(name))) {
      throw new Error("El inventario de descarga no es valido.");
    }
    const startedAt = new Date().toISOString(), reads = {};
    for (const dataset of manifest.datasets) {
      let cursor = null, part = 0;
      const seen = new Set();
      counts[dataset] = 0;
      do {
        check();
        const page = await fetchPage(dataset, cursor, signal);
        check();
        if (page.dataset !== dataset || !Array.isArray(page.rows) ||
            (page.nextCursor != null && (!Array.isArray(page.nextCursor) || !page.nextCursor.length ||
              page.nextCursor.some(x => typeof x !== "string") || !page.rows.length))) {
          throw new Error("La respuesta de descarga esta incompleta.");
        }
        if (JSON.stringify(page.rows).length > PAGE_LIMIT) throw new Error("Una parte de los datos requiere una exportacion asistida por su tamano. No se entrego un archivo incompleto.");
        const references = [];
        const number = String(++part).padStart(6, "0");
        for (let i = 0; i < page.rows.length; i++) {
          await extractImages(page.rows[i], `${dataset}/${number}/${i}`, references);
        }
        const content = strToU8(page.rows.map(row => JSON.stringify(row)).join("\n") + (page.rows.length ? "\n" : ""));
        await add(`datos/${dataset}/${number}.jsonl`, content);
        if (references.length) await add(`imagenes/indice-${dataset}-${number}.json`, strToU8(JSON.stringify(references, null, 2)));
        counts[dataset] += page.rows.length; rowCount += page.rows.length;
        reads[dataset] = { firstReadAt: reads[dataset]?.firstReadAt || page.queriedAt, lastReadAt: page.queriedAt };
        onProgress({ dataset, rows: rowCount, assets: assetCount, datasetsCompleted: Object.keys(reads).length, datasetsTotal: manifest.datasets.length });
        cursor = page.nextCursor;
        if (cursor) {
          const key = JSON.stringify(cursor);
          if (seen.has(key)) throw new Error("La descarga no avanzo. No se entrego un archivo incompleto.");
          seen.add(key);
        }
      } while (cursor);
    }
    await add("LEEME.txt", strToU8(
      "MOZZOAPP - EXPORTACION DE DATOS DEL RESTAURANTE\n\n" +
      "Datos en JSONL: cada linea representa un registro. Los archivos estan agrupados por conjunto y parte.\n" +
      "inventario.json detalla cantidades, campos incluidos/omitidos, modulos no disponibles y SHA-256 de cada archivo.\n" +
      "Las imagenes incrustadas se guardan en imagenes/. Sus indices distinguen archivos incluidos y referencias externas.\n" +
      "No es una copia transaccional de toda la base: los datos se leen durante la descarga. Los registros operativos pueden contener pedidos tambien presentes en otras tablas; no deben sumarse como ventas distintas.\n" +
      "No contiene claves, sesiones, documentos de riders ni copias de terceros. No recupera datos ya eliminados o no sincronizados.\n" +
      "Puede contener datos personales, fiscales y bancarios del restaurante. Guardalo en un lugar privado.\n"
    ));
    const result = { ...manifest, startedAt, finishedAt: new Date().toISOString(), declaredDatasetsDownloaded: true,
      counts, totalRows: rowCount, totalImages: assetCount, reads, files };
    await add("inventario.json", strToU8(JSON.stringify(result, null, 2)), true, false);
    zip.end(); await pending; check();
    // Recheck access after finalization, immediately before committing the local file.
    const finalAccess = await fetchPage("manifest", null, signal);
    check();
    if (finalAccess.companyId !== companyId || JSON.stringify(finalAccess.datasets) !== JSON.stringify(manifest.datasets) ||
        JSON.stringify(finalAccess.schema) !== JSON.stringify(manifest.schema)) {
      throw new Error("Cambio el alcance de la descarga. Volve a intentarlo.");
    }
    await destination.close();
    return result;
  } catch (error) {
    zip.terminate();
    await pending;
    try { await destination.abort(); } catch (_) { /* Preserve the original export error. */ }
    throw error;
  }
}
